/**
 * Shared CGT estimate panel with optional accountant override (RAD share sales + IP sale).
 */
import React, { useState } from 'react';
import { Pencil, RotateCcw } from 'lucide-react';
import { CurrencyInput } from '../../components/ui/index.tsx';
import { formatCurrency } from '../../utils/index.ts';
import { getEffectiveDisposalCgt, parseCgtOverride } from '../../../../shared/src/calculation/entityDisposalTax.ts';
import { isEntityOwnership } from '../../../../shared/src/calculation/constants/clientStructureTypes.ts';
import type { FactFindAsset } from '../../../../shared/src/calculation/factFindTypes.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';

type DraftAsset = FactFindAsset;

/** Result of shared/src/calculation/entityDisposalTax.ts's getEffectiveDisposalCgt (CGT disposal summary + optional accountant override applied). */
export interface DisposalTaxSummary {
    salePrice?: number;
    sellingCosts?: number;
    isPreCGT?: boolean;
    costBase?: number;
    capitalGain?: number;
    assessableCapitalGain?: number;
    estimatedCGT?: number;
    calculatedCGT?: number;
    effectiveCGT?: number;
    netProceeds?: number;
    taxMethod?: 'personal_marginal' | 'trust_distribute' | 'trust_retain' | 'company_rate' | 'partnership_apportioned';
    entityDisposalWarning?: string | null;
    warning?: string | null;
    isOverridden?: boolean;
    clientEstimatedCgt?: number | null;
    partnerEstimatedCgt?: number | null;
    apportionmentClientPct?: number | null;
    apportionmentPartnerPct?: number | null;
    structureName?: string | null;
    [key: string]: unknown;
}

export interface DisposalCgtEstimatePanelProps {
    factFind: DraftFactFind;
    taxSummary: DisposalTaxSummary | null | undefined;
    overrideValue?: number | string | null;
    onOverrideChange?: (value: number | null) => void;
    entityId?: string | null;
    asset?: DraftAsset | null;
    showNetProceedsLine?: boolean;
    saleAmount?: number | string | null;
    netProceedsLabel?: string;
}

export function DisposalCgtEstimatePanel({
    factFind,
    taxSummary,
    overrideValue,
    onOverrideChange,
    entityId = null,
    asset = null,
    showNetProceedsLine = false,
    saleAmount = null,
    netProceedsLabel = 'Net toward RAD (after estimated CGT)',
}: DisposalCgtEstimatePanelProps) {
    const [editingOverride, setEditingOverride] = useState(false);
    const [draftOverride, setDraftOverride] = useState('');

    if (!taxSummary) return null;

    const effective: DisposalTaxSummary = getEffectiveDisposalCgt(taxSummary, overrideValue);
    const isPreCGT = effective.isPreCGT ?? false;
    const capitalGain = effective.capitalGain ?? 0;
    const assessableCapitalGain = effective.assessableCapitalGain ?? 0;
    const capitalGainsTax = effective.estimatedCGT ?? 0;
    const calculatedCgt = effective.calculatedCGT ?? capitalGainsTax;
    const isOverridden = effective.isOverridden ?? false;
    const entityDisposalWarning = effective.entityDisposalWarning || effective.warning;

    const isPartnered = factFind?.adviceFor === 'couple';
    const clientName = factFind?.client?.firstName?.trim() || 'Client';
    const partnerName = factFind?.partner?.firstName?.trim() || 'Partner';
    const showApportionedTaxSplit =
        !isPreCGT &&
        (effective.clientEstimatedCgt != null || effective.partnerEstimatedCgt != null) &&
        ((effective.clientEstimatedCgt ?? 0) > 0 || (effective.partnerEstimatedCgt ?? 0) > 0) &&
        !isOverridden;

    const netAfterTax =
        showNetProceedsLine && saleAmount != null
            ? Math.max(0, parseFloat(String(saleAmount)) || 0) - capitalGainsTax
            : effective.netProceeds;

    const openOverrideEditor = () => {
        const current = parseCgtOverride(overrideValue);
        setDraftOverride(current != null ? String(current) : String(Math.round(calculatedCgt)));
        setEditingOverride(true);
    };

    const applyOverride = () => {
        const parsed = parseCgtOverride(draftOverride);
        onOverrideChange?.(parsed);
        setEditingOverride(false);
    };

    const clearOverride = () => {
        onOverrideChange?.(null);
        setEditingOverride(false);
        setDraftOverride('');
    };

    const showEntityPanels = asset && isEntityOwnership(asset.ownership);

    return (
        <div className="space-y-2">
            <div className="p-3 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-r-lg text-sm">
                {isPreCGT ? (
                    <>
                        <p>
                            <strong className="text-amber-700">Capital Gain:</strong> Not applicable
                            (Pre-CGT asset)
                        </p>
                        <p>
                            <strong className="text-amber-700">Capital Gains Tax:</strong> $0
                        </p>
                    </>
                ) : (
                    <>
                        <p>
                            <strong className="text-amber-700">Capital Gain:</strong>{' '}
                            {formatCurrency(capitalGain)}
                        </p>
                        {effective.taxMethod === 'personal_marginal' && (
                            <p>
                                <strong className="text-amber-700">Assessable Capital Gain:</strong>{' '}
                                {formatCurrency(assessableCapitalGain)} (50% discount)
                            </p>
                        )}
                        {effective.taxMethod === 'trust_distribute' && (
                            <p>
                                <strong className="text-amber-700">
                                    Gain counted for tax (after 50% discount):
                                </strong>{' '}
                                {formatCurrency(assessableCapitalGain)}
                            </p>
                        )}
                        {effective.taxMethod === 'company_rate' && (
                            <p>
                                <strong className="text-amber-700">Tax:</strong> Company rate on full
                                gain (no 50% CGT discount)
                            </p>
                        )}
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                            <p>
                                <strong className="text-amber-700">
                                    {isOverridden ? 'Tax (accountant override):' : 'Estimated tax (total):'}
                                </strong>{' '}
                                {formatCurrency(capitalGainsTax)}
                            </p>
                            {!isPreCGT && (
                                <button
                                    type="button"
                                    onClick={openOverrideEditor}
                                    className="inline-flex items-center gap-1 text-xs font-medium text-slate-700 underline hover:text-slate-900"
                                >
                                    <Pencil size={12} />
                                    {isOverridden ? 'Edit override' : 'Override tax estimate'}
                                </button>
                            )}
                        </div>
                        {isOverridden && calculatedCgt !== capitalGainsTax && (
                            <p className="text-xs text-slate-700 mt-1">
                                Calculated estimate was {formatCurrency(calculatedCgt)}.
                            </p>
                        )}
                        {showApportionedTaxSplit && (
                            <p className="mt-1 text-yellow-900">
                                {isPartnered &&
                                (effective.clientEstimatedCgt ?? 0) > 0 &&
                                (effective.partnerEstimatedCgt ?? 0) > 0 ? (
                                    <>
                                        Split {effective.apportionmentClientPct}% /{' '}
                                        {effective.apportionmentPartnerPct}% (Centrelink on trust card):{' '}
                                        <span className="font-medium">{clientName}</span> ~{' '}
                                        {formatCurrency(effective.clientEstimatedCgt)},{' '}
                                        <span className="font-medium">{partnerName}</span> ~{' '}
                                        {formatCurrency(effective.partnerEstimatedCgt)}
                                    </>
                                ) : (
                                    <>
                                        <span className="font-medium">{clientName}:</span> ~{' '}
                                        {formatCurrency(effective.clientEstimatedCgt)}
                                    </>
                                )}
                            </p>
                        )}
                        {showNetProceedsLine && Number(saleAmount) > 0 && !isPreCGT && (
                            <p className="mt-2 pt-2 border-t border-yellow-200">
                                <strong className="text-amber-700">{netProceedsLabel}:</strong>{' '}
                                {formatCurrency(netAfterTax)}
                            </p>
                        )}
                    </>
                )}
                {editingOverride && (
                    <div className="mt-3 pt-3 border-t border-yellow-200 space-y-2">
                        <p className="text-xs text-yellow-900">
                            Enter the total CGT your accountant advised for this disposal.
                        </p>
                        <div className="flex flex-wrap items-end gap-2">
                            <div className="w-40">
                                <label className="block text-xs font-medium text-yellow-900 mb-1">
                                    Total tax ($)
                                </label>
                                <CurrencyInput
                                    value={draftOverride}
                                    onChange={(v) => setDraftOverride(v === undefined ? '' : String(v))}
                                    placeholder="0"
                                />
                            </div>
                            <button
                                type="button"
                                onClick={applyOverride}
                                className="px-3 py-1.5 text-xs font-semibold rounded bg-yellow-700 text-white hover:bg-yellow-800"
                            >
                                Apply
                            </button>
                            {isOverridden && (
                                <button
                                    type="button"
                                    onClick={clearOverride}
                                    className="inline-flex items-center gap-1 px-2 py-1.5 text-xs font-medium text-yellow-900 hover:underline"
                                >
                                    <RotateCcw size={12} />
                                    Reset to calculated
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => setEditingOverride(false)}
                                className="px-2 py-1.5 text-xs text-slate-700 hover:underline"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                )}
            </div>
            {entityDisposalWarning && (
                <div className="p-3 bg-white border border-slate-200 border-l-4 border-l-amber-400 rounded-r-lg text-sm text-slate-700">
                    {entityDisposalWarning}
                </div>
            )}
            {showEntityPanels && (
                <div className="p-3 bg-orange-50 border-l-4 border-orange-400 rounded-r-lg text-sm text-orange-900">
                    <p>
                        {entityId && effective.taxMethod === 'trust_distribute' ? (
                            isPartnered ? (
                                <>
                                    <span className="font-medium">Family trust:</span> We assume the gain
                                    is paid to {clientName} ({effective.apportionmentClientPct}%) and{' '}
                                    {partnerName} ({effective.apportionmentPartnerPct}%) — same as
                                    Centrelink on your trust card. The trust may pay other{' '}
                                    <span className="font-medium">beneficiaries</span> (e.g. adult
                                    children) instead, which would change the tax. Pension and care fees
                                    still use your Centrelink %.{' '}
                                    <span className="font-medium">
                                        Confirm with your accountant prior to implementing this strategy.
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="font-medium">Family trust:</span> Tax assumes the
                                    gain goes to {clientName} per your Centrelink % on the trust card. The
                                    trust may pay other beneficiaries instead.{' '}
                                    <span className="font-medium">
                                        Confirm with your accountant prior to implementing this strategy.
                                    </span>
                                </>
                            )
                        ) : (
                            <>
                                <span className="font-medium">Trust or company:</span> Sale tax is
                                estimated differently than for your own name.{' '}
                                <span className="font-medium">
                                    Confirm with your accountant prior to implementing this strategy.
                                </span>
                            </>
                        )}
                    </p>
                </div>
            )}
        </div>
    );
}
